import { FormControlLabel, Radio, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { NumericFormat } from 'react-number-format';

import colors from '../../constants/colors';
import { PaymentAmountType } from '../../constants/enums';

interface RadioProps {
  handleClick: (value: PaymentAmountType) => void;
  title: string;
  amount?: number | string;
  selectedValue?: PaymentAmountType;
  testId: string;
  value: PaymentAmountType;
}

const PaymentAmountRadio = ({
  handleClick,
  title,
  amount,
  selectedValue,
  testId,
  value,
}: RadioProps) => {
  const [radioIsFocused, setRadioIsFocused] = useState<boolean>(false);
  return (
    <Stack
      data-testid={title}
      sx={{
        flexDirection: "column",
        borderRadius: 1,
        width: 1,

        border:
          selectedValue === value
            ? '2px solid black'
            : `1px solid ${colors.csaaNeutralBorder}`,

        ...(radioIsFocused && {
          borderColor: colors.csaaLight,
        }),

        outline: radioIsFocused ? `2px solid ${colors.csaaBlack}` : 'none',
        outlineOffset: 2,
        display: 'flex',
        color: colors.csaaDark,

        '&:hover': {
          background: colors.csaaOverlayBlack,
          borderColor: colors.csaaMid,
          color: colors.csaaMid,
        }
      }}>
      <FormControlLabel
        sx={{
          py: 1,
          px: { mobile: 1, tablet: 2 },
          m: 0,
          borderRadius: 1,
        }}
        label={
          <Stack
            sx={{
              flexDirection: "column",
              justifyContent: amount ? 'space-between' : 'center'
            }}>
            <Typography
              sx={{
                fontSize: { mobile: 16, tablet: 18 },
                fontWeight: selectedValue === value ? 700 : 400,
                lineHeight: '130%',
              }}
            >
              {title}
            </Typography>
            {amount && (
              <Typography
                className="csaa-qm-encrypt"
                sx={{
                  fontSize: { mobile: 16, tablet: 18 },
                  mt: { mobile: 0, tablet: 0.5, desktop: 0.5 },
                  fontWeight: 400,
                }}
              >
                <NumericFormat
                  value={amount || 0}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  fixedDecimalScale
                  decimalScale={2}
                />
              </Typography>
            )}
          </Stack>
        }
        value={value}
        disableTypography
        control={
          <Radio
            onFocus={() => setRadioIsFocused(true)}
            onBlur={() => setRadioIsFocused(false)}
            checked={selectedValue === value}
            value={value}
            name={`payment-amount-radio-${value}`}
            data-testid={testId ? `${testId}-radio-input` : testId}
            onChange={() => handleClick(value)}
            sx={{
              p: 0,
              mr: 1,

              color: colors.csaaMid,
              '&.Mui-checked': {
                color: colors.csaaMid,
              },
              '&:hover': {
                background: 'none',
              },
            }}
            slotProps={{
              input: {
                'data-testid': testId
                  ? `${testId}-radio-input`
                  : `payment-amount-radio-${value}`,
                'aria-label': title,
              }
            }}
          />
        }
      />
    </Stack>
  );
};

export default PaymentAmountRadio;
